import { BackHand, CancelPresentation, DoNotDisturb, EmergencyRecording, FiberManualRecord, PanTool, Mic, MicOff, PausePresentationOutlined, PresentToAll, PriorityHigh, ScreenShare, StopRounded, StopScreenShare, TramSharp, Videocam, VideocamOff } from '@mui/icons-material'
import { useMeetingStore } from '../store/meeting.store'
import { useCallback, useMemo } from 'react'
import { useMediaQuery, useTheme } from '@mui/material'
import { useUserStore } from '../store/user.store'
import { Icons } from '../utils/utilities'

export const useToggleButtonsData = () => {

    const MeetingStore = useMeetingStore()
    const { palette, theme, breakpoints } = useTheme()

    const smallScreen = useMediaQuery( breakpoints.down( 'sm' ) )


    const UserStore = useUserStore()

    const buildStyle = useCallback( ( isOn, isDisabled, color = "error" ) => ( {

        background: isOn
            ? "initial"
            : palette[color].dark,

        color: isOn ? palette.common.font : palette.common.white,
        fontSize: "24px",
        border: isOn ? `1px solid ${theme === "dark" ? palette.common.white : "rgba(35,35,35,1)"}` : `1px solid ${palette[color].dark}`,
        paddingX: { xs: "10px", sm: "8px" },
        borderRadius: "20px",

        ":hover": {
            background: isOn
                ? palette.rowHover + "aa"
                : palette[color].dark + "aa",
        },

        ":disabled": {
            border: `1px solid ${palette.grey[500]}`,
            background: "none",
            color: palette.grey[500]
        }
    } ), [palette, theme] )


    return useMemo( () => {

        let { enable_video_feed, enable_screen_sharing, enable_microphone } = MeetingStore.getCurrentMeetingSettings
        let { raisedHand, isSharing, isVideoOn, isAudioOn, isCameraDisabled, isMicDisabled, isRecording, activePresenter, presentingVirtualBoard } = MeetingStore.getMeetingState
        let permission = {}

        const presenting = isSharing || ( presentingVirtualBoard && MeetingStore.getMeetingState.activePresenter?.user_id === MeetingStore.getLocalUser.user_id )

        const localUserPermission = MeetingStore.getUsersPermissions[MeetingStore.getLocalUser.user_id]
        permission = {
            mic: localUserPermission?.mic ?? MeetingStore.getCurrentMeetingSettings.enable_microphone,
            camera: localUserPermission?.camera ?? MeetingStore.getCurrentMeetingSettings.enable_video_feed,
            screenSharing: localUserPermission?.screenSharing ?? MeetingStore.getCurrentMeetingSettings.enable_screen_sharing
        }

        const isHost = ( ["ADMIN", "SUBADMIN"].includes( UserStore?.getUser?.user_role || "" ) ) || Boolean( MeetingStore.getCurrentMeeting.hosts?.find( h => h.user_id === MeetingStore.getLocalUser.user_id ) )

        enable_microphone = isHost ? true : permission.mic && MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class'
        enable_screen_sharing = isHost ? true : permission.screenSharing && MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class'
        enable_video_feed = isHost ? true : permission.camera && MeetingStore.getCurrentMeeting.meeting_type !== 'hybrid-class'

        const cameraToggleButtonProps = {
            tooltip: enable_video_feed ? isCameraDisabled ? `Show details` : `Turn ${isVideoOn ? "Off" : "On"} Camera (CTRL + E)` : `Video feed is disabled for this meeting`,
            disabled: !enable_video_feed,
            badgeContent: enable_video_feed ? isCameraDisabled ? <PriorityHigh sx={{ fontSize: "10px" }} /> : "" : <DoNotDisturb sx={{ fontSize: "10px" }} />,
            hideBadge: enable_video_feed ? isCameraDisabled ? false : true : false,
            badgeColor: enable_video_feed ? isCameraDisabled ? 'warning' : "" : 'error',
            sx: buildStyle( isVideoOn, isCameraDisabled ),
            icon: isVideoOn ? Icons.default.VideocamIcon : Icons.default.VideocamOff,
            clickAction: isCameraDisabled ? "show-details" : "toggle",
            isOn: isVideoOn,
            size: smallScreen ? "small" : "medium"
        }
        const micToggleButtonProps = {
            tooltip: enable_microphone ? isMicDisabled ? `Show details` : `Turn ${isAudioOn ? "Off" : "On"} Mic (CTRL + D)` : `Audio feed is disabled for this meeting`,
            disabled: !enable_microphone,
            badgeContent: enable_microphone ? isMicDisabled ? <PriorityHigh sx={{ fontSize: "10px" }} /> : "" : <DoNotDisturb sx={{ fontSize: "10px" }} />,
            hideBadge: enable_microphone ? isMicDisabled ? false : true : false,
            badgeColor: enable_microphone ? isMicDisabled ? 'warning' : "" : 'error',
            sx: buildStyle( isAudioOn, isMicDisabled ),
            icon: isAudioOn ? Icons.default.Mic : Icons.default.MicOff,
            clickAction: isMicDisabled ? "show-details" : "toggle",
            isOn: isAudioOn,
            size: smallScreen ? "small" : "medium"
        }
        const shareToggleButtonProps = {
            tooltip: enable_screen_sharing ? `${presenting ? "Stop sharing" : "Start presenting"}` : `Screen sharing is disabled for this meeting`,
            disabled: !enable_screen_sharing,
            badgeContent: enable_screen_sharing ? "" : <DoNotDisturb sx={{ fontSize: "10px" }} />,
            hideBadge: enable_screen_sharing ? true : false,
            badgeColor: enable_screen_sharing ? "" : 'error',
            sx: {
                background: presenting ? palette.primary.main : palette.rowHover,
                color: presenting ? palette.common.white : palette.common.font,
                borderRadius: presenting ? "10px" : "20px",
                paddingX: { sm: "15px", xs: "10px" },
                ":hover": {
                    background: presenting ? palette.primary.dark : palette.action.hover
                },
                ":disabled": {
                    border: `1px solid ${palette.grey[500]}`,
                    background: "none",
                    color: palette.grey[500]
                }
            },
            icon: !presenting ? <PresentToAll sx={{ color: "inherit" }} /> : activePresenter?.user_id?.includes( MeetingStore.getLocalUser.user_id ) ? <PresentToAll sx={{ color: "inherit" }} /> : <PausePresentationOutlined sx={{ color: palette.common.font }} />,
            clickAction: "toggle",
            isOn: presenting
        }
        const raiseToggleButtonProps = {
            tooltip: raisedHand ? "Lower your hand (CTRL + ALT + H)" : `Raise your hand (CTRL + ALT + H)`,
            label: raisedHand ? "Lower hand" : `Raise hand`,
            disabled: false,
            sx: {
                borderRadius: raisedHand ? "10px" : "50%",
                background: raisedHand ? palette.secondary.dark : palette.rowHover,
                color: raisedHand ? palette.secondary.contrastText : "",
                borderRadius: "20px",
                paddingX: { sm: "15px", xs: "10px" },
                "& .lower-hand-icon": {
                    transform: raisedHand ? "translate(-2px,-2px)" : "translate(-2px,0)",
                },
                ":hover": {
                    background: raisedHand ? palette.secondary.dark : "",
                },
                ":hover .lower-hand-icon": {
                    transform: raisedHand ? `translate(-2px,2px)` : "translate(-2px,-2px)"
                }
            },
            icon: <PanTool className='lower-hand-icon' sx={{ color: palette.common.font, transition: "transform 200ms ease" }} />,
            clickAction: "toggle",
            isOn: raisedHand
        }

        const recordButtonDetails = {
            startIcon: isRecording ? <FiberManualRecord className='slow-blink' sx={{ color: palette.error.main }} /> : <FiberManualRecord sx={{ color: palette.error.main }} />,
            endIcon: isRecording ? <StopRounded sx={{ color: "inherit" }} /> : <FiberManualRecord sx={{ color: palette.error.main }} />,
            isOn: isRecording,
            tooltip: isRecording ? "Stop Recording" : "Start Recording"
        }

        return {
            cameraButtonDetails: cameraToggleButtonProps,
            micButtonDetails: micToggleButtonProps,
            shareButtonDetails: shareToggleButtonProps,
            raiseHandButtonDetails: raiseToggleButtonProps,
            recordButtonDetails,
        }
        // eslint-disable-next-line
    }, [MeetingStore.getCurrentMeetingSettings, UserStore, buildStyle, MeetingStore.getMeetingState, MeetingStore.getUsersPermissions, theme, palette, MeetingStore, smallScreen] )
}